import { ArrowUp } from 'lucide-react'
import * as React from 'react'
import TextareaAutosize, { type TextareaAutosizeProps } from 'react-textarea-autosize'

import { InputGroup, InputGroupAddon } from '@/components/ui/input-group'
import { SubmitButton } from '@/components/ui/submit-button'
import { AttachmentList } from '@/components/upload/AttachmentList'
import { AttachmentPicker } from '@/components/upload/AttachmentPicker'
import { useFileUploads } from '@/hooks/useFileUploads'
import { cn } from '@/lib/utils'

export interface TextareaSubmittedAttachment {
  documentId: string
  attachmentId: string
  fileName: string
  fileSize: number
  fileExtension: string
  fileUrl: string | null
}

export interface TextareaSubmitPayload {
  message: string
  documentIds: string[]
  attachments: TextareaSubmittedAttachment[]
}

type TextareaWithSubmitProps = TextareaAutosizeProps &
  React.RefAttributes<HTMLTextAreaElement> & {
    buttonProps?: React.ComponentProps<'button'>
    className?: string
    wrapperClassName?: string
    addonChildren?: React.ReactNode
    onValueChange?: (value: string) => void
    enableUploads?: boolean
    /** File uploads under this customer instead of the uploader's own org. */
    uploadOrganisationId?: string
    onSubmitMessage?: (payload: TextareaSubmitPayload) => void
    /** Optional text rendered on the left of the hint row (mirrors the Shift+Enter hint position). */
    leadingHint?: React.ReactNode
    /**
     * Widen the file drop zone to this element instead of just the composer
     * box — e.g. a whole chat panel, so a drop anywhere in it attaches here.
     */
    dropTargetRef?: React.RefObject<HTMLElement | null>
    /** Fires on drag enter/leave so a wider drop zone can highlight itself too. */
    onDraggingChange?: (dragging: boolean) => void
    /**
     * Grow the input box to fill the parent's height rather than sizing to
     * content, for a grid row that has to line up with a taller neighbour.
     * Also lifts the keyboard-hint row out of flow so it stops competing with
     * the box for that height — it just sits under the box as usual.
     */
    fillHeight?: boolean
    /** One rounded line with the send button inside it, for a composer pinned under a chat. */
    pill?: boolean
  }

export function TextareaWithSubmit({
  className,
  wrapperClassName,
  buttonProps,
  onValueChange,
  addonChildren,
  value: controlledValue,
  onChange,
  enableUploads,
  uploadOrganisationId,
  onSubmitMessage,
  leadingHint,
  fillHeight,
  dropTargetRef,
  onDraggingChange,
  pill,
  ...props
}: TextareaWithSubmitProps) {
  const [internalValue, setInternalValue] = React.useState('')
  const value = controlledValue !== undefined ? String(controlledValue) : internalValue
  const hasValue = value.trim().length > 0
  const uploads = useFileUploads({ organisationId: uploadOrganisationId })
  const [isDragging, setIsDragging] = React.useState(false)
  const uploadsEnabled = Boolean(enableUploads)
  const hasUploaded = uploadsEnabled && uploads.uploadedIds.length > 0
  const hasUploading = uploadsEnabled && uploads.hasUploading
  const canSubmit = (hasValue || hasUploaded) && !hasUploading
  const buttonDisabled = buttonProps?.disabled
  const isDisabled = !canSubmit || buttonDisabled
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  // Kept in a ref so a new attachment list doesn't re-bind the drop listeners
  // below mid-drag.
  const addFilesRef = React.useRef(uploads.addFiles)
  React.useEffect(() => {
    addFilesRef.current = uploads.addFiles
  })

  // Native listeners rather than React props: the drop zone may be an ancestor
  // the consumer owns, and the composer box sits inside it — one target means a
  // drop can't be handled twice.
  React.useEffect(() => {
    const target = dropTargetRef?.current ?? wrapperRef.current
    if (!uploadsEnabled || !target) return

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault()
      setIsDragging(true)
    }
    const onDragOver = (e: DragEvent) => e.preventDefault()
    const onDragLeave = (e: DragEvent) => {
      if (e.relatedTarget instanceof Node && target.contains(e.relatedTarget)) return
      setIsDragging(false)
    }
    const onDrop = (e: DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const files = Array.from(e.dataTransfer?.files || [])
      if (files.length > 0) void addFilesRef.current(files)
    }

    target.addEventListener('dragenter', onDragEnter)
    target.addEventListener('dragover', onDragOver)
    target.addEventListener('dragleave', onDragLeave)
    target.addEventListener('drop', onDrop)
    return () => {
      target.removeEventListener('dragenter', onDragEnter)
      target.removeEventListener('dragover', onDragOver)
      target.removeEventListener('dragleave', onDragLeave)
      target.removeEventListener('drop', onDrop)
    }
  }, [dropTargetRef, uploadsEnabled])

  React.useEffect(() => {
    onDraggingChange?.(isDragging)
  }, [isDragging, onDraggingChange])

  function handleSubmit() {
    if (isDisabled) return
    const trimmed = value.trim()
    if (onSubmitMessage) {
      const submittedAttachments: TextareaSubmittedAttachment[] = uploadsEnabled
        ? uploads.attachments
            .filter((a) => a.status === 'uploaded' && a.documentId)
            .map((a) => {
              const name = a.file.name
              const dot = name.lastIndexOf('.')
              const ext = dot === -1 ? '' : name.slice(dot + 1)
              return {
                documentId: a.documentId as string,
                attachmentId: a.attachmentId as string,
                fileName: name,
                fileSize: a.file.size,
                fileExtension: ext,
                fileUrl: a.fileUrl ?? null,
              }
            })
        : []
      onSubmitMessage({
        message: trimmed,
        documentIds: submittedAttachments.map((a) => a.documentId),
        attachments: submittedAttachments,
      })
      if (uploadsEnabled) uploads.clearAll()
      setInternalValue('')
    } else {
      buttonProps?.onClick?.({} as React.MouseEvent<HTMLButtonElement>)
    }
  }

  return (
    <div ref={wrapperRef} className={cn('grid w-full', fillHeight && 'relative h-full', wrapperClassName)}>
      {/* Ring on the box, not the wrapper — the wrapper also spans the
          keyboard-hint row, so it read as floating below the composer. A wider
          drop zone draws its own outline, so the box stays quiet under one. */}
      <InputGroup
        className={cn(
          pill && 'hover:border-ink-300 flex-wrap rounded-2xl',
          uploadsEnabled && isDragging && !dropTargetRef && 'ring-brand-600/60 ring-2 ring-offset-2'
        )}
      >
        {uploadsEnabled && uploads.attachments.length > 0 && (
          <AttachmentList
            attachments={uploads.attachments}
            onRemove={(id) => void uploads.removeAttachment(id)}
          />
        )}
        <TextareaAutosize
          data-slot="input-group-control"
          className={cn(
            'placeholder:text-ink-300 text-md flex field-sizing-content min-h-16 w-full resize-none rounded-md px-3 py-2.5 outline-none md:pointer-fine:text-sm',
            pill && 'min-h-0 w-auto min-w-0 flex-1 rounded-2xl',
            className
          )}
          ref={props.ref}
          maxRows={pill ? 6 : undefined}
          {...props}
          value={value}
          onChange={(e) => {
            setInternalValue(e.target.value)
            onValueChange?.(e.target.value)
            onChange?.(e)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !isDisabled) {
              e.preventDefault()
              buttonRef.current?.click()
            }
            props.onKeyDown?.(e)
          }}
        />
        <InputGroupAddon align={pill ? 'inline-end' : 'block-end'} className="flex items-center">
          {uploadsEnabled && (
            <AttachmentPicker
              onFiles={(files) => void uploads.addFiles(files)}
              disabled={buttonDisabled || hasUploading}
            />
          )}
          <div className="ml-auto flex items-center gap-2">
            {addonChildren}

            <SubmitButton
              ref={buttonRef}
              aria-label="Send"
              className={cn(pill && 'size-8 rounded-full', buttonProps?.className)}
              {...buttonProps}
              disabled={isDisabled}
              onClick={handleSubmit}
            >
              {pill ? <ArrowUp /> : undefined}
            </SubmitButton>
          </div>
        </InputGroupAddon>
      </InputGroup>
      {/* Out of flow when filling: as a second grid item it would otherwise take
          an equal share of the stretched height and leave the box short. */}
      <div
        className={cn(
          'text-muted-foreground mt-1 flex items-center justify-between text-xs',
          fillHeight && 'absolute inset-x-0 top-full'
        )}
      >
        <span>{leadingHint}</span>
        <span className={cn('opacity-0 transition-opacity pointer-coarse:hidden', hasValue && 'opacity-100')}>
          <kbd className="bg-muted rounded border px-1 py-0.5 font-mono text-[10px]">Enter</kbd> to send,{' '}
          <kbd className="bg-muted rounded border px-1 py-0.5 font-mono text-[10px]">Shift</kbd>
          {' + '}
          <kbd className="bg-muted rounded border px-1 py-0.5 font-mono text-[10px]">Enter</kbd> for new line
        </span>
      </div>
    </div>
  )
}
